import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import Header from '../../components/ui/Header';
import Icon from '../../components/AppIcon';
import Button from '../../components/ui/Button';
import RecommendationCard from './components/RecommendationCard';
import DecisionCriteriaPanel from './components/DecisionCriteriaPanel';
import ComparisonTable from './components/ComparisonTable';
import StakeholderImpactAnalysis from './components/StakeholderImpactAnalysis';
import RecommendationDetailsModal from './components/RecommendationDetailsModal';
import FinalDecisionSummary from './components/FinalDecisionSummary';

const DecisionMatrix = () => {
  const navigate = useNavigate();
  const [selectedRecommendationId, setSelectedRecommendationId] = useState(null);
  const [detailsModalOpen, setDetailsModalOpen] = useState(false);
  const [selectedDetailRecommendation, setSelectedDetailRecommendation] = useState(null);
  const [activeTab, setActiveTab] = useState('recommendations');

  const recommendations = [
    {
      id: 'aggressive',
      variant: 'aggressive',
      icon: 'Rocket',
      title: 'Scale Aggressive',
      description: 'Ekspansi cepat dengan investasi besar untuk menangkap peluang pasar maksimal dalam 12-18 bulan',
      riskLevel: 'Tinggi',
      investmentRequired: 'Rp 8,5 - 12 Miliar',
      timeline: '12-18 bulan',
      confidenceScore: 78,
      implementationComplexity: 'Sangat Tinggi',
      expectedROI: '180-220% (3 tahun)',
      expectedOutcomes: [
        'Peningkatan kapasitas produksi 300% dalam 18 bulan',
        'Ekspansi jaringan pemasok dari 45 menjadi 150+ peternak',
        'Penetrasi pasar institusional besar (hotel chain, supermarket)',
        'Pembangunan fasilitas cold storage modern 500 ton',
        'Implementasi sistem ERP terintegrasi penuh'
      ],
      criticalSuccessFactors: [
        {
          title: 'Akses Modal Cepat',
          description: 'Securing Rp 10+ miliar dalam 3 bulan pertama melalui kombinasi equity dan debt financing'
        },
        {
          title: 'Talent Acquisition',
          description: 'Rekrutmen 15+ profesional berpengalaman di supply chain, quality control, dan sales dalam 6 bulan'
        },
        {
          title: 'Supplier Network Expansion',
          description: 'Onboarding 100+ peternak baru dengan standar kualitas tinggi dalam 12 bulan'
        },
        {
          title: 'Technology Infrastructure',
          description: 'Implementasi sistem IoT untuk monitoring real-time kualitas susu dan cold chain management'
        }
      ],
      implementationRequirements: [
        {
          category: 'Infrastruktur',
          icon: 'Building2',
          items: [
            'Cold storage facility 500 ton capacity',
            'Automated processing line dengan kapasitas 5.000 liter/hari',
            'Fleet expansion: 8 refrigerated trucks',
            'Quality control laboratory dengan peralatan modern'
          ]
        },
        {
          category: 'Sumber Daya Manusia',
          icon: 'Users',
          items: [
            'General Manager Operations (1)',
            'Supply Chain Manager (1)',
            'Quality Control Supervisor (2)',
            'Sales & Marketing Team (5)',
            'Technical Staff (15)'
          ]
        },
        {
          category: 'Teknologi',
          icon: 'Cpu',
          items: [
            'ERP system (SAP atau Oracle)',
            'IoT sensors untuk monitoring suhu dan kualitas',
            'Mobile app untuk farmer coordination',
            'Business intelligence dashboard'
          ]
        },
        {
          category: 'Keuangan',
          icon: 'DollarSign',
          items: [
            'Working capital Rp 3 miliar',
            'CAPEX Rp 8 miliar (equipment & facility)',
            'Marketing budget Rp 1,5 miliar/tahun',
            'Contingency fund Rp 2 miliar'
          ]
        }
      ],
      aiAnalysis: 'Analisis AI menunjukkan bahwa strategi aggressive scale memiliki probabilitas sukses 78% dengan asumsi akses modal terpenuhi dan tidak ada supply shock besar dalam 18 bulan pertama. Model prediksi menunjukkan break-even point di bulan ke-22 dengan ROI positif mulai tahun ke-3. Risk utama: ketergantungan pada ekspansi supplier yang cepat dan potensi quality control issues.',
      nextSteps: [
        {
          title: 'Securing Investment & Financial Restructuring',
          description: 'Finalisasi term sheet dengan investor strategis dan bank untuk total funding Rp 12 miliar',
          duration: '0-3 bulan',
          responsible: 'Board of Directors & CFO'
        },
        {
          title: 'Infrastructure Development Kickoff',
          description: 'Mulai konstruksi cold storage facility dan procurement automated processing equipment',
          duration: '3-9 bulan',
          responsible: 'Operations Manager & Project Manager'
        },
        {
          title: 'Supplier Network Expansion Program',
          description: 'Launch program rekrutmen 100+ peternak baru dengan training dan quality assurance',
          duration: '3-12 bulan',
          responsible: 'Supply Chain Manager & Field Coordinators'
        },
        {
          title: 'Technology Implementation & Integration',
          description: 'Deploy ERP system, IoT sensors, dan mobile apps untuk end-to-end visibility',
          duration: '6-15 bulan',
          responsible: 'CTO & IT Team'
        }
      ],
      criticalRisks: [
        'Supplier expansion tidak mencapai target 150 peternak dalam 12 bulan',
        'Quality control issues akibat rapid scaling menyebabkan rejection rate &gt; 15%',
        'Cash flow pressure di bulan 6-18 sebelum revenue ramp-up',
        'Kompetisi dari pemain besar yang merespons aggressive expansion'
      ]
    },
    {
      id: 'controlled',
      variant: 'controlled',
      icon: 'TrendingUp',
      title: 'Scale Controlled',
      description: 'Pertumbuhan bertahap dengan fokus pada stabilitas operasional dan profitabilitas berkelanjutan',
      riskLevel: 'Sedang',
      investmentRequired: 'Rp 3,5 - 5 Miliar',
      timeline: '18-24 bulan',
      confidenceScore: 89,
      implementationComplexity: 'Sedang',
      expectedROI: '120-150% (3 tahun)',
      expectedOutcomes: [
        'Peningkatan kapasitas produksi 150% dalam 24 bulan',
        'Ekspansi jaringan pemasok dari 45 menjadi 80 peternak terseleksi',
        'Penetrasi pasar regional dengan fokus kualitas premium',
        'Upgrade fasilitas cold storage existing menjadi 200 ton',
        'Implementasi sistem manajemen terintegrasi modular'
      ],
      criticalSuccessFactors: [
        {
          title: 'Operational Excellence',
          description: 'Fokus pada efisiensi operasional dan quality control sebelum scaling volume'
        },
        {
          title: 'Supplier Quality Management',
          description: 'Seleksi ketat dan pembinaan intensif 35 peternak baru dengan track record baik'
        },
        {
          title: 'Market Positioning',
          description: 'Membangun brand premium dengan fokus pada kualitas dan konsistensi supply'
        },
        {
          title: 'Financial Discipline',
          description: 'Maintain positive cash flow dan profitability di setiap fase pertumbuhan'
        }
      ],
      implementationRequirements: [
        {
          category: 'Infrastruktur',
          icon: 'Building2',
          items: [
            'Upgrade cold storage existing ke 200 ton',
            'Semi-automated processing line 2.500 liter/hari',
            'Fleet expansion: 4 refrigerated trucks',
            'Quality control lab upgrade'
          ]
        },
        {
          category: 'Sumber Daya Manusia',
          icon: 'Users',
          items: [
            'Operations Manager (1)',
            'Quality Control Lead (1)',
            'Supply Chain Coordinator (1)',
            'Sales Team (3)',
            'Technical Staff (8)'
          ]
        },
        {
          category: 'Teknologi',
          icon: 'Cpu',
          items: [
            'Cloud-based management system',
            'Temperature monitoring IoT',
            'Supplier coordination platform',
            'Basic analytics dashboard'
          ]
        },
        {
          category: 'Keuangan',
          icon: 'DollarSign',
          items: [
            'Working capital Rp 1,5 miliar',
            'CAPEX Rp 3,5 miliar',
            'Marketing budget Rp 800 juta/tahun',
            'Contingency fund Rp 1 miliar'
          ]
        }
      ],
      aiAnalysis: 'Model AI memberikan confidence score tertinggi 89% untuk strategi controlled scale karena balance optimal antara growth dan risk mitigation. Proyeksi menunjukkan break-even di bulan ke-16 dengan cash flow positif konsisten mulai bulan ke-20. Strategi ini paling resilient terhadap market volatility dan supply disruptions.',
      nextSteps: [
        {
          title: 'Operational Optimization & Quality Enhancement',
          description: 'Audit menyeluruh proses existing dan implementasi SOP baru untuk quality assurance',
          duration: '0-3 bulan',
          responsible: 'Operations Manager & Quality Control Lead'
        },
        {
          title: 'Selective Supplier Expansion',
          description: 'Rekrutmen dan training 35 peternak baru dengan kriteria ketat dan monitoring intensif',
          duration: '3-12 bulan',
          responsible: 'Supply Chain Coordinator'
        },
        {
          title: 'Infrastructure Upgrade Phase 1',
          description: 'Upgrade cold storage dan processing line dengan fokus pada efficiency dan quality',
          duration: '6-15 bulan',
          responsible: 'Operations Manager & Technical Team'
        },
        {
          title: 'Market Development & Brand Building',
          description: 'Launch premium product line dan penetrasi pasar regional dengan positioning kualitas',
          duration: '9-24 bulan',
          responsible: 'Sales & Marketing Team'
        }
      ],
      criticalRisks: [
        'Pertumbuhan lebih lambat dari kompetitor dapat mengurangi market share',
        'Supplier expansion terbatas dapat membatasi growth potential',
        'Premium positioning memerlukan konsistensi kualitas tinggi yang challenging',
        'Investment return lebih lambat dibanding aggressive strategy'
      ]
    },
    {
      id: 'hold',
      variant: 'hold',
      icon: 'Pause',
      title: 'Hold & Optimize',
      description: 'Fokus pada stabilisasi operasional dan perbaikan fundamental sebelum ekspansi lebih lanjut',
      riskLevel: 'Rendah',
      investmentRequired: 'Rp 1 - 1,5 Miliar',
      timeline: '12-18 bulan',
      confidenceScore: 82,
      implementationComplexity: 'Rendah',
      expectedROI: '60-80% (3 tahun)',
      expectedOutcomes: [
        'Stabilisasi supply chain dengan rejection rate &lt; 5%',
        'Optimalisasi kapasitas existing tanpa ekspansi besar',
        'Perbaikan margin operasional menjadi 18-22%',
        'Konsolidasi hubungan dengan 45 supplier existing',
        'Implementasi sistem monitoring dan quality control dasar'
      ],
      criticalSuccessFactors: [
        {
          title: 'Process Optimization',
          description: 'Eliminasi waste dan inefficiency dalam operasional existing untuk improve margin'
        },
        {
          title: 'Supplier Relationship Strengthening',
          description: 'Fokus pada pembinaan dan stabilisasi 45 supplier existing dengan payment reliability'
        },
        {
          title: 'Quality Consistency',
          description: 'Achieve consistent quality standard dengan rejection rate minimal'
        },
        {
          title: 'Cost Management',
          description: 'Strict cost control dan working capital optimization untuk maintain profitability'
        }
      ],
      implementationRequirements: [
        {
          category: 'Infrastruktur',
          icon: 'Building2',
          items: [
            'Maintenance dan repair equipment existing',
            'Minor upgrade cold storage efficiency',
            'Optimalisasi layout produksi',
            'Basic quality control tools'
          ]
        },
        {
          category: 'Sumber Daya Manusia',
          icon: 'Users',
          items: [
            'Operations Supervisor (1)',
            'Quality Control Staff (1)',
            'Supplier Coordinator (1)',
            'Technical Staff (3)'
          ]
        },
        {
          category: 'Teknologi',
          icon: 'Cpu',
          items: [
            'Spreadsheet-based tracking system',
            'Basic temperature monitoring',
            'WhatsApp-based supplier coordination',
            'Simple reporting tools'
          ]
        },
        {
          category: 'Keuangan',
          icon: 'DollarSign',
          items: [
            'Working capital Rp 800 juta',
            'Maintenance CAPEX Rp 500 juta',
            'Training budget Rp 200 juta',
            'Contingency fund Rp 500 juta'
          ]
        }
      ],
      aiAnalysis: 'Strategi hold & optimize direkomendasikan jika analisis menunjukkan fundamental weakness yang perlu diperbaiki sebelum scaling. AI confidence 82% bahwa strategi ini akan stabilize operations dan improve profitability dalam 12-18 bulan. Risk minimal dengan focus pada internal improvement tanpa external expansion pressure.',
      nextSteps: [
        {
          title: 'Comprehensive Operational Audit',
          description: 'Deep dive analysis semua proses untuk identifikasi bottleneck dan inefficiency',
          duration: '0-2 bulan',
          responsible: 'Operations Supervisor & External Consultant'
        },
        {
          title: 'Process Improvement Implementation',
          description: 'Execute quick wins dan systematic improvement berdasarkan audit findings',
          duration: '2-8 bulan',
          responsible: 'Operations Supervisor & Technical Team'
        },
        {
          title: 'Supplier Relationship Program',
          description: 'Structured engagement dengan 45 supplier existing untuk improve quality dan reliability',
          duration: '3-12 bulan',
          responsible: 'Supplier Coordinator'
        },
        {
          title: 'Financial Optimization',
          description: 'Working capital management dan cost reduction initiatives untuk improve margin',
          duration: '0-18 bulan',
          responsible: 'Finance Manager'
        }
      ],
      criticalRisks: [
        'Kompetitor dapat mengambil market share selama periode hold',
        'Supplier dapat beralih ke buyer lain jika tidak ada growth signal',
        'Team morale dapat menurun tanpa growth trajectory yang jelas',
        'Window of opportunity untuk scaling dapat tertutup'
      ]
    },
    {
      id: 'restructure',
      variant: 'restructure',
      icon: 'RefreshCw',
      title: 'Restructure & Pivot',
      description: 'Restrukturisasi fundamental model bisnis dengan fokus pada segmen pasar atau produk berbeda',
      riskLevel: 'Tinggi',
      investmentRequired: 'Rp 2 - 3 Miliar',
      timeline: '18-24 bulan',
      confidenceScore: 65,
      implementationComplexity: 'Sangat Tinggi',
      expectedROI: '40-60% (3 tahun)',
      expectedOutcomes: [
        'Pivot ke segmen pasar niche dengan margin lebih tinggi',
        'Restrukturisasi supplier network dengan fokus kualitas premium',
        'Diversifikasi produk ke dairy derivatives (yogurt, cheese)',
        'Rightsizing operasional untuk profitabilitas berkelanjutan',
        'Implementasi business model baru dengan value proposition berbeda'
      ],
      criticalSuccessFactors: [
        {
          title: 'Strategic Clarity',
          description: 'Clear definition of new business model dan target market dengan differentiation jelas'
        },
        {
          title: 'Stakeholder Alignment',
          description: 'Buy-in dari board, management, dan key suppliers untuk perubahan fundamental'
        },
        {
          title: 'Execution Discipline',
          description: 'Systematic implementation of restructuring plan dengan milestone tracking ketat'
        },
        {
          title: 'Financial Runway',
          description: 'Adequate funding untuk sustain operations selama transition period 18-24 bulan'
        }
      ],
      implementationRequirements: [
        {
          category: 'Infrastruktur',
          icon: 'Building2',
          items: [
            'Facility reconfiguration untuk new product line',
            'Specialized equipment untuk dairy derivatives',
            'Downsizing atau repurposing existing assets',
            'New quality control setup'
          ]
        },
        {
          category: 'Sumber Daya Manusia',
          icon: 'Users',
          items: [
            'Change Management Lead (1)',
            'Product Development Manager (1)',
            'Restructuring Consultant (external)',
            'Selective team retention dan new hires'
          ]
        },
        {
          category: 'Teknologi',
          icon: 'Cpu',
          items: [
            'New product development tools',
            'Market research dan analytics',
            'Reconfigured management system',
            'Customer feedback platform'
          ]
        },
        {
          category: 'Keuangan',
          icon: 'DollarSign',
          items: [
            'Restructuring cost Rp 1 miliar',
            'New product CAPEX Rp 1,5 miliar',
            'Working capital Rp 1 miliar',
            'Contingency fund Rp 1,5 miliar'
          ]
        }
      ],
      aiAnalysis: 'Strategi restructure memiliki confidence score 65% karena high uncertainty dalam market acceptance dan execution complexity. AI analysis menunjukkan bahwa pivot strategy hanya viable jika current model fundamentally broken atau market opportunity sangat compelling. Success rate historical untuk dairy business restructuring adalah 40-50%.',
      nextSteps: [
        {
          title: 'Strategic Review & New Business Model Design',
          description: 'Comprehensive analysis dan design new business model dengan clear value proposition',
          duration: '0-3 bulan',
          responsible: 'Board of Directors & Strategy Consultant'
        },
        {
          title: 'Stakeholder Communication & Alignment',
          description: 'Transparent communication dengan semua stakeholders dan secure buy-in untuk changes',
          duration: '2-4 bulan',
          responsible: 'CEO & Change Management Lead'
        },
        {
          title: 'Phased Restructuring Implementation',
          description: 'Execute restructuring plan dalam phases dengan clear milestones dan go/no-go decisions',
          duration: '6-18 bulan',
          responsible: 'Change Management Lead & Operations Team'
        },
        {
          title: 'New Market Entry & Product Launch',
          description: 'Launch new products atau enter new market segments dengan focused marketing',
          duration: '12-24 bulan',
          responsible: 'Product Development Manager & Sales Team'
        }
      ],
      criticalRisks: [
        'Execution failure dapat mengakibatkan business collapse',
        'Supplier dan customer attrition selama transition period',
        'Cash burn rate tinggi tanpa revenue certainty',
        'Team morale dan retention issues selama uncertainty period',
        'Market acceptance untuk new model tidak sesuai proyeksi'
      ]
    }
  ];

  const decisionCriteria = [
    {
      type: 'financial',
      name: 'Kapasitas Keuangan',
      description: 'Kemampuan akses modal dan cash flow untuk mendukung investasi',
      weight: 85
    },
    {
      type: 'operational',
      name: 'Kesiapan Operasional',
      description: 'Infrastruktur, SDM, dan sistem untuk eksekusi strategi',
      weight: 78
    },
    {
      type: 'market',
      name: 'Peluang Pasar',
      description: 'Demand potential dan competitive landscape',
      weight: 72
    },
    {
      type: 'risk',
      name: 'Toleransi Risiko',
      description: 'Appetite stakeholder terhadap risiko dan volatilitas',
      weight: 68
    },
    {
      type: 'timeline',
      name: 'Urgency Timeline',
      description: 'Tekanan waktu untuk capture market opportunity',
      weight: 55
    },
    {
      type: 'resources',
      name: 'Ketersediaan Sumber Daya',
      description: 'Talent, supplier network, dan strategic partners',
      weight: 62
    }
  ];

  const stakeholders = [
    {
      type: 'board',
      name: 'Board of Directors',
      description: 'Pemegang keputusan strategis dan approval investasi besar',
      impactLevel: 'Positif Tinggi',
      primaryConcern: 'ROI dan long-term value creation dengan risk mitigation yang jelas',
      potentialRisk: 'Ketidaksepakatan dalam risk appetite dapat menghambat keputusan cepat',
      mitigationStrategy: 'Presentasi komprehensif dengan scenario analysis dan clear risk-return trade-offs'
    },
    {
      type: 'management',
      name: 'Management Team',
      description: 'Eksekutor strategi dengan tanggung jawab implementasi harian',
      impactLevel: 'Positif Sedang',
      primaryConcern: 'Feasibility implementasi dan ketersediaan resources untuk eksekusi',
      potentialRisk: 'Overload dan burnout jika scaling terlalu aggressive tanpa support memadai',
      mitigationStrategy: 'Phased implementation dengan clear resource allocation dan external support'
    },
    {
      type: 'farmers',
      name: 'Peternak Pemasok',
      description: '45 peternak existing dan potential 100+ peternak baru',
      impactLevel: 'Positif Tinggi',
      primaryConcern: 'Stabilitas pembelian, harga fair, dan payment reliability',
      potentialRisk: 'Supplier attrition jika ekspansi terlalu cepat mengganggu relationship existing',
      mitigationStrategy: 'Transparent communication, incentive program, dan gradual onboarding new suppliers'
    },
    {
      type: 'employees',
      name: 'Karyawan Operasional',
      description: 'Team produksi, quality control, dan logistik',
      impactLevel: 'Netral',
      primaryConcern: 'Job security, workload manageability, dan career development',
      potentialRisk: 'Resistance to change dan turnover jika restructuring terlalu disruptive',
      mitigationStrategy: 'Training program, clear communication, dan retention incentives'
    },
    {
      type: 'customers',
      name: 'Institutional Buyers',
      description: 'Hotel, restaurant, supermarket, dan distributor',
      impactLevel: 'Positif Sedang',
      primaryConcern: 'Consistency supply, quality assurance, dan competitive pricing',
      potentialRisk: 'Quality issues atau supply disruption selama scaling dapat damage reputation',
      mitigationStrategy: 'Gradual capacity increase dengan quality control ketat dan transparent communication'
    },
    {
      type: 'investors',
      name: 'Potential Investors',
      description: 'VC, private equity, atau strategic investors untuk funding',
      impactLevel: 'Positif Tinggi',
      primaryConcern: 'Clear growth trajectory, exit strategy, dan governance structure',
      potentialRisk: 'Dilution ownership dan loss of control jika external funding terlalu besar',
      mitigationStrategy: 'Selective investor dengan strategic value-add dan favorable terms negotiation'
    }
  ];

  const handleSelectRecommendation = (id) => {
    setSelectedRecommendationId(id);
  };

  const handleViewDetails = (id) => {
    const recommendation = recommendations?.find(rec => rec?.id === id);
    setSelectedDetailRecommendation(recommendation);
    setDetailsModalOpen(true);
  };

  const handleExportReport = () => {
    alert('Fungsi export PDF akan diimplementasikan dengan library seperti jsPDF atau react-pdf');
  };

  const handleProceedToImplementation = () => {
    navigate('/action-plan-dashboard');
  };

  const selectedRecommendation = recommendations?.find(rec => rec?.id === selectedRecommendationId);

  const tabs = [
    { id: 'recommendations', label: 'Rekomendasi Strategis', icon: 'Target' },
    { id: 'comparison', label: 'Perbandingan', icon: 'GitCompare' },
    { id: 'criteria', label: 'Kriteria Keputusan', icon: 'Scale' },
    { id: 'stakeholders', label: 'Analisis Stakeholder', icon: 'Users' },
    { id: 'decision', label: 'Keputusan Final', icon: 'CheckCircle2' }
  ];

  return (
    <div className="min-h-screen bg-background">
      <Header />
      <main className="pt-20">
        <div className="container-executive section-spacing">
          <div className="text-center mb-12">
            <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-primary/10 text-primary text-sm font-medium mb-4">
              <Icon name="Target" size={16} />
              <span>Decision Matrix</span>
            </div>
            <h1 className="mb-4">Matriks Keputusan Strategis</h1>
            <p className="text-lg text-muted-foreground max-w-3xl mx-auto">
              Analisis komprehensif empat opsi strategis untuk scale-up bisnis dairy dengan evaluasi risiko, investasi, dan proyeksi return yang terukur
            </p>
          </div>

          <div className="mb-8">
            <div className="card p-0 overflow-hidden">
              <div className="flex overflow-x-auto">
                {tabs?.map((tab) => (
                  <button
                    key={tab?.id}
                    onClick={() => setActiveTab(tab?.id)}
                    className={`flex items-center gap-2 px-6 py-4 text-sm font-medium whitespace-nowrap transition-all duration-250 border-b-2 ${
                      activeTab === tab?.id
                        ? 'text-primary border-primary bg-primary/5' :'text-muted-foreground border-transparent hover:text-foreground hover:bg-muted'
                    }`}
                  >
                    <Icon name={tab?.icon} size={18} />
                    <span>{tab?.label}</span>
                  </button>
                ))}
              </div>
            </div>
          </div>

          {activeTab === 'recommendations' && (
            <div className="space-y-8">
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                {recommendations?.map((recommendation) => (
                  <RecommendationCard
                    key={recommendation?.id}
                    recommendation={recommendation}
                    isSelected={selectedRecommendationId === recommendation?.id}
                    onSelect={handleSelectRecommendation}
                    onViewDetails={handleViewDetails}
                  />
                ))}
              </div>

              {selectedRecommendationId && (
                <div className="p-6 rounded-xl bg-success/5 border border-success/20">
                  <div className="flex items-start gap-4">
                    <Icon name="CheckCircle2" size={24} color="var(--color-success)" className="flex-shrink-0 mt-1" />
                    <div>
                      <h3 className="text-lg font-semibold text-success mb-2">Rekomendasi Terpilih</h3>
                      <p className="text-sm text-muted-foreground mb-4">
                        Anda telah memilih strategi <strong>{selectedRecommendation?.title}</strong>. Silakan review detail lengkap di tab "Keputusan Final" atau lanjutkan ke perbandingan untuk analisis lebih mendalam.
                      </p>
                      <div className="flex flex-wrap gap-3">
                        <Button
                          variant="default"
                          size="sm"
                          onClick={() => setActiveTab('decision')}
                          iconName="ArrowRight"
                          iconPosition="right"
                        >
                          Lihat Keputusan Final
                        </Button>
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => setActiveTab('comparison')}
                          iconName="GitCompare"
                          iconPosition="left"
                        >
                          Bandingkan Opsi
                        </Button>
                      </div>
                    </div>
                  </div>
                </div>
              )}
            </div>
          )}

          {activeTab === 'comparison' && (
            <div className="space-y-6">
              <ComparisonTable recommendations={recommendations} />
              
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                <div className="card">
                  <div className="flex items-center gap-3 mb-6">
                    <div className="w-12 h-12 rounded-xl bg-success/10 flex items-center justify-center">
                      <Icon name="TrendingUp" size={24} color="var(--color-success)" />
                    </div>
                    <div>
                      <h3 className="text-lg font-semibold">Proyeksi ROI Komparatif</h3>
                      <p className="text-sm text-muted-foreground">Return on Investment 3 tahun</p>
                    </div>
                  </div>
                  <div className="space-y-4">
                    {recommendations?.map((rec) => (
                      <div key={rec?.id} className="flex items-center justify-between p-3 rounded-lg bg-muted/50">
                        <div className="flex items-center gap-3">
                          <Icon name={rec?.icon} size={20} color="var(--color-primary)" />
                          <span className="text-sm font-medium">{rec?.title}</span>
                        </div>
                        <span className="text-sm font-bold data-text">{rec?.expectedROI}</span>
                      </div>
                    ))}
                  </div>
                </div>

                <div className="card">
                  <div className="flex items-center gap-3 mb-6">
                    <div className="w-12 h-12 rounded-xl bg-warning/10 flex items-center justify-center">
                      <Icon name="AlertTriangle" size={24} color="var(--color-warning)" />
                    </div>
                    <div>
                      <h3 className="text-lg font-semibold">Risk-Return Matrix</h3>
                      <p className="text-sm text-muted-foreground">Analisis risiko vs return</p>
                    </div>
                  </div>
                  <div className="space-y-4">
                    {recommendations?.map((rec) => (
                      <div key={rec?.id} className="p-3 rounded-lg border border-border">
                        <div className="flex items-center justify-between mb-2">
                          <span className="text-sm font-medium">{rec?.title}</span>
                          <span className={`px-2 py-1 rounded text-xs font-medium ${
                            rec?.riskLevel === 'Rendah' ? 'bg-success/10 text-success' :
                            rec?.riskLevel === 'Sedang'? 'bg-warning/10 text-warning' : 'bg-destructive/10 text-destructive'
                          }`}>
                            {rec?.riskLevel}
                          </span>
                        </div>
                        <div className="flex items-center gap-2 text-xs text-muted-foreground">
                          <span>Confidence:</span>
                          <div className="flex-1 h-1.5 bg-muted rounded-full overflow-hidden">
                            <div 
                              className="h-full bg-primary"
                              style={{ width: `${rec?.confidenceScore}%` }}
                            />
                          </div>
                          <span className="font-semibold data-text">{rec?.confidenceScore}%</span>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          )}

          {activeTab === 'criteria' && (
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
              <div className="lg:col-span-2">
                <DecisionCriteriaPanel criteria={decisionCriteria} />
              </div>
              
              <div className="space-y-6">
                <div className="card">
                  <div className="flex items-center gap-3 mb-6">
                    <div className="w-12 h-12 rounded-xl bg-primary/10 flex items-center justify-center">
                      <Icon name="Brain" size={24} color="var(--color-primary)" />
                    </div>
                    <div>
                      <h3 className="text-lg font-semibold">AI Scoring</h3>
                      <p className="text-sm text-muted-foreground">Confidence levels</p>
                    </div>
                  </div>
                  <div className="space-y-3">
                    {recommendations?.map((rec) => (
                      <div key={rec?.id} className="p-3 rounded-lg bg-muted/50">
                        <div className="flex items-center justify-between mb-2">
                          <span className="text-sm font-medium">{rec?.title}</span>
                          <span className="text-sm font-bold data-text text-primary">
                            {rec?.confidenceScore}%
                          </span>
                        </div>
                        <div className="w-full h-2 bg-card rounded-full overflow-hidden">
                          <div 
                            className="h-full bg-primary transition-all duration-500"
                            style={{ width: `${rec?.confidenceScore}%` }}
                          />
                        </div>
                      </div>
                    ))}
                  </div>
                </div>

                <div className="card">
                  <div className="flex items-center gap-3 mb-4">
                    <Icon name="Info" size={20} color="var(--color-primary)" />
                    <h4 className="text-sm font-semibold">Metodologi AI</h4>
                  </div>
                  <p className="text-xs text-muted-foreground leading-relaxed">
                    Sistem AI menganalisis 50+ data points dari operational, financial, dan market assessment untuk menghasilkan confidence score. Model machine learning dilatih dengan 200+ case studies dairy business scale-up di Indonesia dan Asia Tenggara.
                  </p>
                </div>
              </div>
            </div>
          )}

          {activeTab === 'stakeholders' && (
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
              <div className="lg:col-span-2">
                <StakeholderImpactAnalysis stakeholders={stakeholders} />
              </div>
              
              <div className="space-y-6">
                <div className="card">
                  <div className="flex items-center gap-3 mb-6">
                    <div className="w-12 h-12 rounded-xl bg-primary/10 flex items-center justify-center">
                      <Icon name="Users" size={24} color="var(--color-primary)" />
                    </div>
                    <div>
                      <h3 className="text-lg font-semibold">Stakeholder Map</h3>
                      <p className="text-sm text-muted-foreground">Influence vs Interest</p>
                    </div>
                  </div>
                  <div className="space-y-3">
                    {stakeholders?.map((stakeholder, index) => (
                      <div key={index} className="flex items-center justify-between p-3 rounded-lg bg-muted/50">
                        <div className="flex items-center gap-2">
                          <Icon name={stakeholder?.type === 'board' ? 'Users' : 
                                     stakeholder?.type === 'management' ? 'Briefcase' :
                                     stakeholder?.type === 'farmers' ? 'Tractor' :
                                     stakeholder?.type === 'employees' ? 'UserCheck' :
                                     stakeholder?.type === 'customers' ? 'ShoppingCart' : 'TrendingUp'} 
                                size={16} 
                                color="var(--color-primary)" />
                          <span className="text-sm font-medium">{stakeholder?.name}</span>
                        </div>
                        <span className={`px-2 py-1 rounded text-xs font-medium ${
                          stakeholder?.impactLevel?.includes('Positif Tinggi') ? 'bg-success/10 text-success' :
                          stakeholder?.impactLevel?.includes('Positif Sedang') ? 'bg-primary/10 text-primary' :
                          stakeholder?.impactLevel?.includes('Netral') ? 'bg-muted text-muted-foreground' :
                          'bg-warning/10 text-warning'
                        }`}>
                          {stakeholder?.impactLevel}
                        </span>
                      </div>
                    ))}
                  </div>
                </div>

                <div className="card">
                  <div className="flex items-center gap-3 mb-4">
                    <Icon name="AlertCircle" size={20} color="var(--color-warning)" />
                    <h4 className="text-sm font-semibold">Key Considerations</h4>
                  </div>
                  <ul className="space-y-2 text-xs text-muted-foreground">
                    <li className="flex items-start gap-2">
                      <span className="text-warning mt-1">•</span>
                      <span>Board approval critical untuk investasi &gt; Rp 5 miliar</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <span className="text-warning mt-1">•</span>
                      <span>Farmer retention rate harus &gt; 85% selama scaling</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <span className="text-warning mt-1">•</span>
                      <span>Employee engagement survey setiap quarter</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <span className="text-warning mt-1">•</span>
                      <span>Customer satisfaction monitoring real-time</span>
                    </li>
                  </ul>
                </div>
              </div>
            </div>
          )}

          {activeTab === 'decision' && (
            <div className="space-y-6">
              <FinalDecisionSummary
                selectedRecommendation={selectedRecommendation}
                onExportReport={handleExportReport}
                onProceedToImplementation={handleProceedToImplementation}
              />

              {selectedRecommendation && (
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                  <div className="card">
                    <div className="flex items-center gap-3 mb-6">
                      <div className="w-12 h-12 rounded-xl bg-primary/10 flex items-center justify-center">
                        <Icon name="FileText" size={24} color="var(--color-primary)" />
                      </div>
                      <div>
                        <h3 className="text-lg font-semibold">Dokumentasi Keputusan</h3>
                        <p className="text-sm text-muted-foreground">Record untuk audit trail</p>
                      </div>
                    </div>
                    <div className="space-y-3 text-sm">
                      <div className="flex justify-between p-3 rounded-lg bg-muted/50">
                        <span className="text-muted-foreground">Tanggal Keputusan</span>
                        <span className="font-medium">01 Februari 2026</span>
                      </div>
                      <div className="flex justify-between p-3 rounded-lg bg-muted/50">
                        <span className="text-muted-foreground">Decision Maker</span>
                        <span className="font-medium">Board of Directors</span>
                      </div>
                      <div className="flex justify-between p-3 rounded-lg bg-muted/50">
                        <span className="text-muted-foreground">AI Confidence</span>
                        <span className="font-medium data-text">{selectedRecommendation?.confidenceScore}%</span>
                      </div>
                      <div className="flex justify-between p-3 rounded-lg bg-muted/50">
                        <span className="text-muted-foreground">Review Date</span>
                        <span className="font-medium">01 Mei 2026</span>
                      </div>
                    </div>
                  </div>

                  <div className="card">
                    <div className="flex items-center gap-3 mb-6">
                      <div className="w-12 h-12 rounded-xl bg-warning/10 flex items-center justify-center">
                        <Icon name="AlertTriangle" size={24} color="var(--color-warning)" />
                      </div>
                      <div>
                        <h3 className="text-lg font-semibold">Monitoring KPIs</h3>
                        <p className="text-sm text-muted-foreground">Metrics untuk track progress</p>
                      </div>
                    </div>
                    <div className="space-y-3">
                      <div className="p-3 rounded-lg border border-border">
                        <div className="flex items-center justify-between mb-1">
                          <span className="text-sm font-medium">Revenue Growth</span>
                          <span className="text-xs text-muted-foreground">Target: +150%</span>
                        </div>
                        <div className="text-xs text-muted-foreground">Monthly tracking vs baseline</div>
                      </div>
                      <div className="p-3 rounded-lg border border-border">
                        <div className="flex items-center justify-between mb-1">
                          <span className="text-sm font-medium">Supplier Network Size</span>
                          <span className="text-xs text-muted-foreground">Target: 80 farmers</span>
                        </div>
                        <div className="text-xs text-muted-foreground">Quarterly onboarding review</div>
                      </div>
                      <div className="p-3 rounded-lg border border-border">
                        <div className="flex items-center justify-between mb-1">
                          <span className="text-sm font-medium">Quality Rejection Rate</span>
                          <span className="text-xs text-muted-foreground">Target: &lt; 5%</span>
                        </div>
                        <div className="text-xs text-muted-foreground">Daily monitoring dashboard</div>
                      </div>
                      <div className="p-3 rounded-lg border border-border">
                        <div className="flex items-center justify-between mb-1">
                          <span className="text-sm font-medium">Operating Margin</span>
                          <span className="text-xs text-muted-foreground">Target: 18-22%</span>
                        </div>
                        <div className="text-xs text-muted-foreground">Monthly P&amp;L analysis</div>
                      </div>
                    </div>
                  </div>
                </div>
              )}
            </div>
          )}

          <div className="mt-12 flex flex-col sm:flex-row justify-between items-center gap-4 p-6 rounded-xl bg-muted/50">
            <div className="flex items-center gap-3">
              <Icon name="Info" size={24} color="var(--color-primary)" />
              <div>
                <p className="text-sm font-medium">Butuh bantuan dalam pengambilan keputusan?</p>
                <p className="text-xs text-muted-foreground">Konsultasikan dengan tim advisory kami</p>
              </div>
            </div>
            <Button variant="outline" iconName="MessageCircle" iconPosition="left">
              Hubungi Advisory Team
            </Button>
          </div>
        </div>
      </main>
      <RecommendationDetailsModal
        recommendation={selectedDetailRecommendation}
        isOpen={detailsModalOpen}
        onClose={() => setDetailsModalOpen(false)}
      />
    </div>
  );
};

export default DecisionMatrix;